import { useAtomValue, useSetAtom } from 'jotai';
import { useCallback } from 'react';

import { useGetTelehealthSessionInfo } from '../../hooks';
import { useAtomCallbackMemoized } from '../../hooks/useAtomCallbackMemoized';
import { useVideoPlatform } from '../../hooks/useVideoPlatform';
import { unreadMessagesCountFamily } from '../../session/state';
import { VideoPlatform, normalizeVideoPlatform, participantCountAtom } from '../../video-sdk';
import { useVerifyClientCompatibilityCallback } from '../admission/useVerifyClientCompatibilityCallback';
import { useOpenConversationCallback } from '../chat/useOpenConversationCallback';
import { useOpenClientInformationCallback } from '../client-information/useClientInformationDrawerCallbacks';
import {
  inSessionPatientInformationAtom,
  joiningVisitorIdFamily,
  newSessionConfirmationDataAtom,
  providerShortIdAtom,
  waitingRoomConversationSidsByVisitorIdAtom,
  waitingRoomConversationSidsByVisitorIdFamily,
  waitingVisitorsAtom,
} from '../state';
import { ClientQueue, ClientQueueActions, ClientQueueActionsProps } from './ClientQueue';
import { openWaitingRoomConversation } from './openWaitingRoomConversation';
import { useAdmitClientCallback } from './useAdmitClientCallback';

export function ClientQueueActionsWrapper({
  visitorName,
  visitorUuid,
  patientShortId,
  providerVideoPlatform,
}: Pick<ClientQueueActionsProps, 'visitorName' | 'patientShortId'> & {
  visitorUuid: string;
  visitorVideoPlatform?: VideoPlatform | null;
  providerVideoPlatform?: VideoPlatform | null;
}) {
  const inSession = !!useAtomValue(participantCountAtom);
  const [admitClient] = useAdmitClientCallback();
  const isZoom = providerVideoPlatform === VideoPlatform.Zoom;
  const setNewSessionConfirmationData = useSetAtom(newSessionConfirmationDataAtom);

  const waitingRoomConversationSid = useAtomValue(waitingRoomConversationSidsByVisitorIdFamily(visitorUuid));
  const { patientShortId: currentSessionPatientShortId = null } = useAtomValue(inSessionPatientInformationAtom) ?? {};
  const getWaitingRoomConversationSids = useAtomCallbackMemoized(
    (get) => get(waitingRoomConversationSidsByVisitorIdAtom),
    [],
  );

  const loading = useAtomValue(joiningVisitorIdFamily(visitorUuid));
  const openConversation = useOpenConversationCallback();

  const openClientInformation = useOpenClientInformationCallback();

  const openClientInformationSection = useCallback(() => {
    openClientInformation({ visitorUuid, patientShortId });
  }, [openClientInformation, visitorUuid, patientShortId]);

  const verifyClientCompatibility = useVerifyClientCompatibilityCallback();
  const unreadMessagesCount = useAtomValue(unreadMessagesCountFamily(waitingRoomConversationSid));

  const providerShortId = useAtomValue(providerShortIdAtom);
  const { data, loading: isAppointmentLoading } = useGetTelehealthSessionInfo(providerShortId, patientShortId);
  const appointment = data?.telehealthSessionInfo?.appointment;

  const isIncompatibleClientUser = !verifyClientCompatibility(visitorUuid);

  return (
    <ClientQueueActions
      {...{
        appointment,
        currentSessionPatientShortId,
        inSession,
        isAppointmentLoading,
        isConversationLoading: !waitingRoomConversationSid,
        isIncompatible: isIncompatibleClientUser,
        isZoom,
        loading,
        onOpenClientInformationDrawer: openClientInformationSection,
        onChat: () => {
          openWaitingRoomConversation(visitorUuid, getWaitingRoomConversationSids, openConversation);
        },
        onAdmit: async () => {
          admitClient({
            visitorId: visitorUuid,
            visitorName,
            patientShortId,
          });
        },
        onShowStartNewSessionConfirmation: () => {
          setNewSessionConfirmationData({
            visitorName,
            visitorUuid,
            patientShortId,
            variation: isIncompatibleClientUser ? 'incompatible' : 'default',
          });
        },
        unreadMessagesCount,
        visitorName,
        visitorUuid,
        patientShortId,
      }}
    />
  );
}

export function ClientQueueWrapper() {
  const waitingClients = useAtomValue(waitingVisitorsAtom);
  const providerVideoPlatform = normalizeVideoPlatform(useVideoPlatform());

  return (
    <ClientQueue
      clients={waitingClients}
      clientActionsComponent={ClientQueueActionsWrapper}
      providerVideoPlatform={providerVideoPlatform}
    />
  );
}
